// The Stripe-Checkout-return decision, factored out of App.tsx's boot effect so
// the restore/janitor/notice branching is unit-testable (App.tsx just executes
// the result + does the side effects).
//
// `pending` is the already-validated stash from loadPendingRun (it returns null
// for a stale/empty/unreadable record), so the decision only sees a USABLE stash
// or null.
//
//   janitor          — plain boot (no ?paid/?canceled): purge any orphaned stash.
//   paid-launch      — paid + usable stash, same code: restore the file and
//                       auto-launch.
//   paid-reload      — paid but stash gone: the code is paid; ask them to re-load.
//   canceled-restore — canceled + stash, same code: restore the file, show a
//                       calm notice.
//   canceled-none    — canceled, nothing stashed: nothing to do.
//   stash-mismatch   — stash exists but was minted under a DIFFERENT access
//                       code than the one now current: never restore or
//                       auto-launch (identity-safety finding, launch-audit
//                       2026-07-04 — see below).

import type { PendingRun } from "./pending-run";
import { normalizeAccessCode } from "./access-code";

export type CheckoutReturnAction =
  | { kind: "janitor" }
  | { kind: "paid-launch"; pending: PendingRun }
  | { kind: "paid-reload" }
  | { kind: "canceled-restore"; pending: PendingRun }
  | { kind: "canceled-none" }
  | { kind: "stash-mismatch" };

export function decideCheckoutReturn(args: {
  paid: boolean;
  canceled: boolean;
  pending: PendingRun | null;
  // The access code currently in effect for THIS mount (App's `accessCode`
  // state, read once from localStorage at boot). Compared against the
  // stash's own accessCode so a stash minted for a DIFFERENT code — a second
  // tab on the same origin validating a new code while this tab's checkout
  // was in flight, or a bookmarked/revisited ?paid=1 URL after the stored
  // code changed — can never restore or auto-launch under the wrong code.
  // Without this check, `paid-launch`/`canceled-restore` would hand tab A's
  // message corpus to tab B's code: case-content misattribution, with
  // results routed to the wrong email.
  currentAccessCode: string;
}): CheckoutReturnAction {
  const { paid, canceled, pending, currentAccessCode } = args;
  if (!paid && !canceled) return { kind: "janitor" };
  if (
    pending &&
    normalizeAccessCode(pending.accessCode) !==
      normalizeAccessCode(currentAccessCode)
  ) {
    return { kind: "stash-mismatch" };
  }
  if (paid) {
    return pending
      ? { kind: "paid-launch", pending }
      : { kind: "paid-reload" };
  }
  // canceled
  return pending
    ? { kind: "canceled-restore", pending }
    : { kind: "canceled-none" };
}
